import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { usePerformance } from '../hooks/usePerformance';

const FloatingParticles = () => {
  const { isLowPerformance, isReducedMotion } = usePerformance();
  const [particles, setParticles] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const animationRef = useRef();
  const lastTimeRef = useRef(0);

  // Generar partículas tipo luciérnagas azules más realistas
  const generateFireflies = useMemo(() => {
    if (isLowPerformance || isReducedMotion) return [];
    
    const fireflies = [];
    const particleCount = isLowPerformance ? 25 : 50;
    
    for (let i = 0; i < particleCount; i++) {
      fireflies.push({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        centerX: Math.random() * 100, // Centro de la órbita actual
        centerY: Math.random() * 100, // Centro de la órbita actual
        size: Math.random() * 4 + 3,
        duration: Math.random() * 20 + 15,
        delay: Math.random() * 20,
        opacity: Math.random() * 0.4 + 0.6,
        glowIntensity: Math.random() * 0.6 + 0.4,
        movementPattern: Math.floor(Math.random() * 3),
        pulseSpeed: Math.random() * 3 + 2,
        twinkleSpeed: Math.random() * 4 + 3,
        avoidanceRadius: 120, // Radio de evasión reducido para menos precisión
        avoidanceStrength: Math.random() * 0.3 + 0.1, // Fuerza muy reducida para suavidad
        time: Math.random() * Math.PI * 2, // Tiempo inicial para movimiento
        isCurrentlyAvoiding: false, // Estado de evasión actual
        avoidanceStartTime: 0, // Tiempo de inicio de evasión
        avoidanceDelay: Math.random() * 200 + 100, // Delay más largo entre 100-300ms
        avoidanceCooldown: 0, // Cooldown para evitar reacciones bruscas
        naturalMovementSpeed: Math.random() * 0.8 + 0.6, // Velocidad natural aumentada
        transitionSpeed: Math.random() * 0.9 + 0.7, // Velocidad de transición aumentada
        returnToNaturalTime: 0, // Tiempo para volver al movimiento natural
        isReturningToNatural: false, // Estado de retorno al movimiento natural
        lastVelocityX: 0, // Velocidad anterior en X
        lastVelocityY: 0 // Velocidad anterior en Y
      });
    }
    
    return fireflies;
  }, [isLowPerformance, isReducedMotion]);

  // Manejar el movimiento del mouse con throttling
  const handleMouseMove = useCallback((e) => {
    setMousePosition({
      x: e.clientX,
      y: e.clientY
    });
  }, []);

  // Calcular la evasión del cursor con suavizado extremo
  const calculateAvoidance = useCallback((particle, mouseX, mouseY, currentTime) => {
    const particleX = particle.x * window.innerWidth / 100;
    const particleY = particle.y * window.innerHeight / 100;
    const dx = mouseX - particleX;
    const dy = mouseY - particleY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Solo reaccionar si está muy cerca y no está en cooldown
    if (distance < particle.avoidanceRadius && currentTime > particle.avoidanceCooldown) {
      // Si no está evitando, verificar el delay
      if (!particle.isCurrentlyAvoiding && !particle.isReturningToNatural) {
        const timeSinceLastCheck = currentTime - (particle.avoidanceStartTime || 0);
        if (timeSinceLastCheck > particle.avoidanceDelay) {
          // Calcular fuerza de evasión muy suave
          const force = Math.min((particle.avoidanceRadius - distance) / particle.avoidanceRadius, 0.5);
          const angle = Math.atan2(dy, dx);
          const avoidanceForce = force * 2 * particle.avoidanceStrength; // Fuerza muy reducida
          
          // Calcular nueva posición con suavizado extremo
          const newX = particle.x - Math.cos(angle) * avoidanceForce;
          const newY = particle.y - Math.sin(angle) * avoidanceForce;
          
          return {
            x: newX,
            y: newY,
            centerX: newX, // Actualizar centro gradualmente
            centerY: newY,
            isCurrentlyAvoiding: true,
            isReturningToNatural: false,
            avoidanceStartTime: currentTime,
            avoidanceCooldown: currentTime + 500, // 500ms de cooldown
            returnToNaturalTime: 0,
            lastVelocityX: newX - particle.x,
            lastVelocityY: newY - particle.y
          };
        }
      } else if (particle.isCurrentlyAvoiding && !particle.isReturningToNatural) {
        // Si ya está evitando, continuar con evasión muy suave
        const force = Math.min((particle.avoidanceRadius - distance) / particle.avoidanceRadius, 0.3);
        const angle = Math.atan2(dy, dx);
        const avoidanceForce = force * 1.5 * particle.avoidanceStrength; // Fuerza aún más suave
        
        const newX = particle.x - Math.cos(angle) * avoidanceForce;
        const newY = particle.y - Math.sin(angle) * avoidanceForce;
        
        return {
          x: newX,
          y: newY,
          centerX: particle.centerX + (newX - particle.x) * particle.transitionSpeed, // Transición gradual
          centerY: particle.centerY + (newY - particle.y) * particle.transitionSpeed,
          isCurrentlyAvoiding: true,
          isReturningToNatural: false,
          avoidanceStartTime: particle.avoidanceStartTime,
          avoidanceCooldown: particle.avoidanceCooldown,
          returnToNaturalTime: 0,
          lastVelocityX: newX - particle.x,
          lastVelocityY: newY - particle.y
        };
      }
    } else {
      // Si el cursor está lejos, iniciar transición suave hacia movimiento natural
      if (particle.isCurrentlyAvoiding && !particle.isReturningToNatural) {
        const timeSinceAvoidance = currentTime - particle.avoidanceStartTime;
        if (timeSinceAvoidance > 300) { // Cooldown más corto para transición suave
          return {
            x: particle.x,
            y: particle.y,
            centerX: particle.centerX,
            centerY: particle.centerY,
            isCurrentlyAvoiding: false,
            isReturningToNatural: true,
            avoidanceStartTime: 0,
            avoidanceCooldown: currentTime + 200, // Cooldown más corto
            returnToNaturalTime: currentTime,
            lastVelocityX: particle.lastVelocityX,
            lastVelocityY: particle.lastVelocityY
          };
        }
      }
    }
    
    return {
      x: particle.x,
      y: particle.y,
      centerX: particle.centerX,
      centerY: particle.centerY,
      isCurrentlyAvoiding: particle.isCurrentlyAvoiding,
      isReturningToNatural: particle.isReturningToNatural,
      avoidanceStartTime: particle.avoidanceStartTime,
      avoidanceCooldown: particle.avoidanceCooldown,
      returnToNaturalTime: particle.returnToNaturalTime,
      lastVelocityX: particle.lastVelocityX,
      lastVelocityY: particle.lastVelocityY
    };
  }, []);

  // Función de animación principal con transiciones suaves
  const animateParticles = useCallback((currentTime) => {
    if (isLowPerformance || isReducedMotion) return;

    const deltaTime = currentTime - lastTimeRef.current;
    lastTimeRef.current = currentTime;

    setParticles(prevParticles => 
      prevParticles.map(particle => {
        // Movimiento natural constante basado en el centro actual
        const time = particle.time + deltaTime * 0.001 * particle.naturalMovementSpeed; // Velocidad natural variable
        let naturalX = particle.centerX;
        let naturalY = particle.centerY;

        // Aplicar movimiento según el patrón alrededor del centro actual
        switch (particle.movementPattern) {
          case 0: // Circular suave
            const radius = 2;
            naturalX = particle.centerX + Math.cos(time * 0.5) * radius;
            naturalY = particle.centerY + Math.sin(time * 0.5) * radius;
            break;
          case 1: // Zigzag suave
            naturalX = particle.centerX + Math.sin(time * 0.8) * 3;
            naturalY = particle.centerY + Math.cos(time * 0.6) * 2;
            break;
          case 2: // Flotante muy suave
            naturalX = particle.centerX + Math.sin(time * 0.3) * 1.5;
            naturalY = particle.centerY + Math.cos(time * 0.4) * 1.5;
            break;
        }

        // Aplicar evasión del cursor con suavizado extremo
        const avoidance = calculateAvoidance(particle, mousePosition.x, mousePosition.y, currentTime);
        
        // Transición suave entre evasión y movimiento natural
        let finalX, finalY, finalCenterX, finalCenterY;
        
        if (avoidance.isCurrentlyAvoiding) {
          // Si está evitando, usar posición de evasión con transición suave
          finalX = particle.x + (avoidance.x - particle.x) * particle.transitionSpeed;
          finalY = particle.y + (avoidance.y - particle.y) * particle.transitionSpeed;
          finalCenterX = avoidance.centerX;
          finalCenterY = avoidance.centerY;
        } else if (avoidance.isReturningToNatural) {
          // Si está volviendo al movimiento natural, mantener velocidad y suavizar transición
          const returnProgress = Math.min((currentTime - avoidance.returnToNaturalTime) / 800, 1); // 800ms de transición
          const easeProgress = returnProgress * returnProgress * (3 - 2 * returnProgress); // Easing suave
          
          // Mantener la velocidad anterior y mezclarla con el movimiento natural
          const velocityInfluence = 1 - easeProgress;
          const naturalInfluence = easeProgress;
          
          finalX = particle.x + (naturalX - particle.x) * naturalInfluence + particle.lastVelocityX * velocityInfluence * 0.5;
          finalY = particle.y + (naturalY - particle.y) * naturalInfluence + particle.lastVelocityY * velocityInfluence * 0.5;
          finalCenterX = particle.centerX;
          finalCenterY = particle.centerY;
          
          // Si la transición está completa, resetear el estado
          if (returnProgress >= 1) {
            return {
              ...particle,
              x: finalX,
              y: finalY,
              centerX: finalCenterX,
              centerY: finalCenterY,
              isCurrentlyAvoiding: false,
              isReturningToNatural: false,
              avoidanceStartTime: 0,
              avoidanceCooldown: 0,
              returnToNaturalTime: 0,
              lastVelocityX: 0,
              lastVelocityY: 0,
              time: time
            };
          }
        } else {
          // Movimiento natural normal con velocidad completa
          finalX = particle.x + (naturalX - particle.x) * particle.transitionSpeed;
          finalY = particle.y + (naturalY - particle.y) * particle.transitionSpeed;
          finalCenterX = particle.centerX;
          finalCenterY = particle.centerY;
        }

        // Mantener dentro de los límites con suavizado
        const clampedX = Math.max(0, Math.min(100, finalX));
        const clampedY = Math.max(0, Math.min(100, finalY));
        const clampedCenterX = Math.max(0, Math.min(100, finalCenterX));
        const clampedCenterY = Math.max(0, Math.min(100, finalCenterY));

        return {
          ...particle,
          x: clampedX,
          y: clampedY,
          centerX: clampedCenterX,
          centerY: clampedCenterY,
          isCurrentlyAvoiding: avoidance.isCurrentlyAvoiding,
          isReturningToNatural: avoidance.isReturningToNatural,
          avoidanceStartTime: avoidance.avoidanceStartTime,
          avoidanceCooldown: avoidance.avoidanceCooldown,
          returnToNaturalTime: avoidance.returnToNaturalTime,
          lastVelocityX: avoidance.lastVelocityX,
          lastVelocityY: avoidance.lastVelocityY,
          time: time
        };
      })
    );

    animationRef.current = requestAnimationFrame(animateParticles);
  }, [calculateAvoidance, mousePosition, isLowPerformance, isReducedMotion]);

  // Iniciar animación
  useEffect(() => {
    if (isLowPerformance || isReducedMotion) return;

    animationRef.current = requestAnimationFrame(animateParticles);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animateParticles, isLowPerformance, isReducedMotion]);

  useEffect(() => {
    setParticles(generateFireflies);
  }, [generateFireflies]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  if (isLowPerformance || isReducedMotion) {
    return null;
  }

  return (
    <div className="modern-floating-particles">
      {particles.map((particle) => (
        <div
          key={particle.id}
          className={`firefly-particle firefly-${particle.movementPattern} firefly-blue`}
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            animationDuration: `${particle.duration}s`,
            animationDelay: `${particle.delay}s`,
            opacity: particle.opacity,
            '--glow-intensity': particle.glowIntensity,
            '--pulse-speed': `${particle.pulseSpeed}s`,
            '--twinkle-speed': `${particle.twinkleSpeed}s`,
            transition: 'none' // Sin transición CSS para control total con JS
          }}
        >
          {/* Núcleo brillante de la luciérnaga */}
          <div className="firefly-core"></div>
          
          {/* Aura de luz azul */}
          <div className="firefly-aura"></div>
          
          {/* Efecto de parpadeo suave */}
          <div className="firefly-twinkle"></div>
          
          {/* Efecto de brillo adicional */}
          <div className="firefly-shimmer"></div>
        </div>
      ))}
    </div>
  );
};

export default FloatingParticles; 